import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import allAcronyms from "@/data/allAcronyms";
import {
  Wand2, Lock, Check, ArrowRight, ArrowLeft, ShoppingBag, Download,
  Pen, Plus, X, Mail, User, Gift, Sparkles, BookOpen, RotateCcw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FULL_UNLOCK_STRIPE = "https://buy.stripe.com/5kQ28k7DS1BJaee9SSc7u0m";

const categoryLabels = {
  core: "Core Framework",
  personal: "Personal Codes",
  tools: "Tools & Values",
  family: "Family Codes",
  business: "Business Codes",
  wellness: "Wellness & Healing",
  empowerment: "Empowerment Codes",
};

const categoryColors = {
  core: "from-[#25a1c2] to-[#042F2E]",
  personal: "from-[#7C3AED] to-[#4F46E5]",
  tools: "from-[#B45309] to-[#D97706]",
  family: "from-[#EA580C] to-[#F59E0B]",
  business: "from-[#1E40AF] to-[#3B82F6]",
  wellness: "from-[#25a1c2] to-[#068fa2]",
  empowerment: "from-[#DC2626] to-[#E11D48]",
};

const steps = ["browse", "customize", "generate"];

export default function CodeBuilder() {
  const [step, setStep] = useState("browse");
  const [selectedAcronyms, setSelectedAcronyms] = useState([]);
  const [customLetters, setCustomLetters] = useState([]);
  const [userName, setUserName] = useState("");
  const [codeType, setCodeType] = useState("Personal Code of Conduct");
  const [isPaid, setIsPaid] = useState(false);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [emailData, setEmailData] = useState({ firstName: "", email: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resultRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("mbsh_code_paid")) setIsPaid(true);
    if (localStorage.getItem("mbsh_code_email")) setShowEmailGate(false);
  }, []);

  const freeAcronyms = allAcronyms.filter(a => a.tier === "free");
  const paidAcronyms = allAcronyms.filter(a => a.tier === "paid");
  const availableAcronyms = isPaid ? allAcronyms : freeAcronyms;

  const toggleAcronym = (code) => {
    if (selectedAcronyms.includes(code)) {
      setSelectedAcronyms(selectedAcronyms.filter(c => c !== code));
    } else {
      setSelectedAcronyms([...selectedAcronyms, code]);
    }
  };

  const goToCustomize = () => {
    const letters = [];
    selectedAcronyms.forEach(code => {
      const acr = allAcronyms.find(a => a.code === code);
      if (acr) {
        acr.letters.forEach(l => {
          letters.push({
            source: acr.code,
            letter: l.letter,
            word: l.word,
            originalDef: l.definition,
            customDef: "",
            included: true,
          });
        });
      }
    });
    setCustomLetters(letters);
    setStep("customize");
    window.scrollTo(0, 0);
  };

  const addCustomLetter = () => {
    setCustomLetters([...customLetters, {
      source: "CUSTOM",
      letter: "",
      word: "",
      originalDef: "",
      customDef: "",
      included: true,
    }]);
  };

  const updateCustomLetter = (index, field, value) => {
    const updated = [...customLetters];
    updated[index][field] = value;
    setCustomLetters(updated);
  };

  const removeLetter = (index) => {
    setCustomLetters(customLetters.filter((_, i) => i !== index));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/subscribe`, emailData);
    } catch {}
    localStorage.setItem("mbsh_code_email", "true");
    setShowEmailGate(false);
    setIsSubmitting(false);
    setStep("generate");
    window.scrollTo(0, 0);
  };

  const goToGenerate = () => {
    if (!localStorage.getItem("mbsh_code_email")) {
      setShowEmailGate(true);
    } else {
      setStep("generate");
      window.scrollTo(0, 0);
    }
  };

  const activeLetters = customLetters.filter(l => l.included);

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="code-builder-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#CCFBF1] print:hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/downloads/MBSH-logo-TM-e4476e.png"
              alt="Mind Body Spirit HYGIENE™ Tools"
              className="h-12 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/build-your-own" className="text-[#068fa2] hover:text-[#25a1c2] text-sm font-medium transition-colors" data-testid="codebuilder-nav-build-your-own">Build Your Own</Link>
            {step !== "browse" && (
              <button
                onClick={() => { setStep(step === "generate" ? "customize" : "browse"); window.scrollTo(0,0); }}
                className="text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
            <Link to="/acronym-dictionary" className="text-[#068fa2] hover:text-[#25a1c2] text-sm transition-colors hidden md:block">
              Acronym Dictionary
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="fixed top-[65px] w-full z-40 bg-white border-b border-[#CCFBF1] print:hidden">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-4">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
                step === s ? "bg-[#25a1c2] text-white" :
                steps.indexOf(step) > i ? "bg-[#5EEAD4] text-[#042F2E]" : "bg-gray-200 text-gray-400"
              }`}>
                {steps.indexOf(step) > i ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${step === s ? "text-[#042F2E]" : "text-gray-400"}`}>
                {s === "browse" ? "Choose Codes" : s === "customize" ? "Make It Yours" : "Your Code"}
              </span>
              {i < 2 && <div className="flex-1 h-0.5 bg-gray-200 hidden sm:block" />}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pt-32 pb-16">

        {/* STEP 1: BROWSE */}
        {step === "browse" && (
          <div data-testid="step-browse">
            <div className="text-center mb-10">
              <Wand2 className="w-10 h-10 text-[#25a1c2] mx-auto mb-4" />
              <h1 className="font-serif text-3xl md:text-4xl text-[#042F2E] tracking-tight mb-3" data-testid="builder-title">
                Build Your Code of Conduct
              </h1>
              <p className="text-[#475569] max-w-xl mx-auto">
                Choose the acronyms that resonate with you. Mix and match. Use your family's last name, 
                a word that inspires you — anything. Then customize each letter with YOUR definitions.
              </p>
              <p className="text-[#25a1c2] text-sm font-medium mt-3 italic">
                "Try on the tools like hats — see what fits for you."
              </p>
            </div>

            {/* Code Type */}
            <div className="flex gap-2 mb-8 justify-center flex-wrap">
              {["Personal Code of Conduct", "Family Code of Conduct", "Business Code of Conduct"].map(t => (
                <button
                  key={t}
                  onClick={() => setCodeType(t)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    codeType === t ? "bg-[#25a1c2] text-white" : "bg-white border border-[#CCFBF1] text-[#475569] hover:border-[#25a1c2]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Available Acronyms */}
            {Object.entries(categoryLabels).map(([cat, label]) => {
              const catAcronyms = availableAcronyms.filter(a => a.category === cat);
              const lockedCat = paidAcronyms.filter(a => a.category === cat && !isPaid);
              if (catAcronyms.length === 0 && lockedCat.length === 0) return null;

              return (
                <div key={cat} className="mb-8">
                  <h3 className="font-bold text-[#042F2E] text-sm uppercase tracking-widest mb-3">{label}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {catAcronyms.map(acr => (
                      <button
                        key={acr.code}
                        onClick={() => toggleAcronym(acr.code)}
                        data-testid={`select-${acr.code.toLowerCase().replace(/[^a-z]/g, "")}`}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          selectedAcronyms.includes(acr.code)
                            ? "border-[#25a1c2] bg-[#F0FDF4] shadow-md"
                            : "border-[#CCFBF1] bg-white hover:border-[#25a1c2]/50"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-[#042F2E]">{acr.code}</span>
                          {selectedAcronyms.includes(acr.code) && <Check className="w-4 h-4 text-[#25a1c2]" />}
                        </div>
                        <p className="text-xs text-[#475569]">{acr.title}</p>
                        <p className="text-xs text-[#25a1c2] mt-1">{acr.letters.length} letters</p>
                      </button>
                    ))}
                    {lockedCat.map(acr => (
                      <div key={acr.code} className="p-4 rounded-xl border-2 border-gray-200 bg-gray-50 text-left relative overflow-hidden opacity-60">
                        <Lock className="w-4 h-4 text-gray-300 absolute top-3 right-3" />
                        <span className="font-bold text-gray-400">{acr.code}</span>
                        <p className="text-xs text-gray-400">{acr.title}</p>
                        <p className="text-xs text-gray-300 mt-1">{acr.letters.length} letters</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Unlock CTA */}
            {!isPaid && (
              <div className="bg-[#042F2E] rounded-2xl p-6 text-center mb-8">
                <Lock className="w-6 h-6 text-[#5EEAD4] mx-auto mb-2" />
                <h3 className="text-white font-bold mb-2">Unlock All 38 Acronyms</h3>
                <p className="text-white/60 text-sm mb-4">Get the full dictionary + Code Builder access for $8</p>
                <a
                  href={FULL_UNLOCK_STRIPE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#5EEAD4] hover:bg-[#99F6E4] text-[#042F2E] rounded-full px-6 py-3 font-bold text-sm transition-all hover:-translate-y-0.5"
                  data-testid="unlock-all-btn"
                >
                  <ShoppingBag className="w-4 h-4" /> Unlock All 38 — $8
                </a>
              </div>
            )}

            {/* Continue Button */}
            <div className="text-center">
              <button
                onClick={goToCustomize}
                disabled={selectedAcronyms.length === 0}
                data-testid="continue-to-customize"
                className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-bold transition-all hover:-translate-y-0.5 shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Customize {selectedAcronyms.length > 0 ? `${selectedAcronyms.length} Code${selectedAcronyms.length > 1 ? "s" : ""}` : "Your Code"}
                <ArrowRight className="w-5 h-5" />
              </button>
              {selectedAcronyms.length === 0 && (
                <p className="text-[#475569] text-xs mt-3">Select at least one acronym to continue</p>
              )}
            </div>
          </div>
        )}

        {/* STEP 2: CUSTOMIZE */}
        {step === "customize" && !showEmailGate && (
          <div data-testid="step-customize">
            <div className="text-center mb-8">
              <Pen className="w-8 h-8 text-[#25a1c2] mx-auto mb-3" />
              <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E] mb-2">Make It Yours</h2>
              <p className="text-[#475569] text-sm max-w-lg mx-auto">
                Edit, rewrite, or add your own definitions. There are NO limits — write a word, a sentence, or a paragraph.
                Use your family name, a favorite word, anything that speaks to YOUR authentic self.
              </p>
            </div>

            <div className="mb-6">
              <label className="text-xs text-[#475569] font-bold uppercase tracking-widest">Your Name (for the document)</label>
              <input
                type="text"
                value={userName}
                onChange={e => setUserName(e.target.value)}
                placeholder="Temple Franklin"
                data-testid="user-name-input"
                className="w-full mt-1 px-4 py-3 rounded-xl border border-[#CCFBF1] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#25a1c2]"
              />
            </div>

            <div className="space-y-4 mb-8">
              {customLetters.map((item, i) => (
                <div key={i} className={`bg-white rounded-xl border ${item.included ? "border-[#CCFBF1]" : "border-gray-200 opacity-50"} p-4`}>
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col gap-1">
                      {item.source === "CUSTOM" ? (
                        <input
                          type="text"
                          value={item.letter}
                          onChange={e => updateCustomLetter(i, "letter", e.target.value.toUpperCase())}
                          placeholder="A"
                          maxLength={5}
                          className="w-12 h-12 rounded-xl bg-[#25a1c2] text-white font-bold text-center text-sm focus:outline-none"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-[#25a1c2] flex items-center justify-center">
                          <span className="text-white font-bold text-sm">{item.letter}</span>
                        </div>
                      )}
                      <span className="text-[9px] text-gray-400 text-center">{item.source}</span>
                    </div>
                    <div className="flex-1">
                      {item.source === "CUSTOM" ? (
                        <input
                          type="text"
                          value={item.word}
                          onChange={e => updateCustomLetter(i, "word", e.target.value)}
                          placeholder="Your word..."
                          className="w-full font-bold text-[#042F2E] text-sm mb-1 px-2 py-1 rounded border border-[#CCFBF1] focus:outline-none focus:ring-1 focus:ring-[#25a1c2]"
                        />
                      ) : (
                        <p className="font-bold text-[#042F2E] text-sm mb-1">{item.word}</p>
                      )}
                      {item.originalDef && !item.customDef && (
                        <p className="text-[#475569] text-xs leading-relaxed mb-2 italic">{item.originalDef}</p>
                      )}
                      <textarea
                        value={item.customDef}
                        onChange={e => updateCustomLetter(i, "customDef", e.target.value)}
                        placeholder="Write your own definition... no limits!"
                        rows={2}
                        className="w-full text-sm px-3 py-2 rounded-lg border border-dashed border-[#CCFBF1] bg-[#efeae7] focus:outline-none focus:ring-1 focus:ring-[#25a1c2] resize-y"
                      />
                    </div>
                    <button
                      onClick={() => removeLetter(i)}
                      className="text-gray-300 hover:text-red-400 transition-colors mt-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addCustomLetter}
              data-testid="add-custom-letter"
              className="w-full border-2 border-dashed border-[#CCFBF1] hover:border-[#25a1c2] rounded-xl py-4 text-[#25a1c2] font-medium text-sm flex items-center justify-center gap-2 transition-all mb-8"
            >
              <Plus className="w-4 h-4" /> Add Your Own Letter
            </button>

            <div className="text-center">
              <button
                onClick={goToGenerate}
                data-testid="generate-code-btn"
                className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-bold transition-all hover:-translate-y-0.5 shadow-lg"
              >
                <Sparkles className="w-5 h-5" /> Generate My Code
              </button>
            </div>
          </div>
        )}

        {/* EMAIL GATE */}
        {showEmailGate && (
          <div className="text-center" data-testid="builder-email-gate">
            <div className="bg-[#042F2E] rounded-3xl p-8 md:p-10 max-w-md mx-auto">
              <Gift className="w-10 h-10 text-[#5EEAD4] mx-auto mb-4" />
              <h2 className="font-serif text-2xl text-white mb-3">Almost There!</h2>
              <p className="text-white/70 text-sm mb-6">
                Enter your email to generate your code — plus get Temple's free 52-week 
                "I GET TO" email journey and a bonus 365-day affirmation calendar.
              </p>
              <form onSubmit={handleEmailSubmit} className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5EEAD4]" />
                  <input type="text" placeholder="First Name" required value={emailData.firstName}
                    onChange={e => setEmailData({...emailData, firstName: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#5EEAD4]" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5EEAD4]" />
                  <input type="email" placeholder="Email" required value={emailData.email}
                    onChange={e => setEmailData({...emailData, email: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#5EEAD4]" />
                </div>
                <button type="submit" disabled={isSubmitting}
                  className="w-full bg-[#5EEAD4] hover:bg-[#99F6E4] text-[#042F2E] rounded-xl py-3.5 font-bold text-sm transition-all disabled:opacity-50">
                  {isSubmitting ? "Creating..." : "Generate My Code"}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* STEP 3: GENERATE */}
        {step === "generate" && (
          <div data-testid="step-generate">
            <div className="text-center mb-6 print:hidden">
              <button
                onClick={() => window.print()}
                data-testid="print-code-btn"
                className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-6 py-3 font-bold text-sm transition-all"
              >
                <Download className="w-4 h-4" /> Print / Save as PDF
              </button>
              <button
                onClick={() => { setStep("browse"); setSelectedAcronyms([]); setCustomLetters([]); setUserName(""); window.scrollTo(0,0); }}
                className="ml-3 inline-flex items-center gap-1 text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Start Over
              </button>
            </div>

            {/* The Generated Document */}
            <div ref={resultRef} className="bg-white rounded-2xl border-2 border-[#25a1c2]/20 p-8 md:p-12 print:border-0 print:p-8">
              <div className="text-center mb-10">
                <p className="text-[#25a1c2] text-xs font-bold tracking-widest uppercase mb-4">Mind Body Spirit HYGIENE™ Tools</p>
                <h1 className="font-serif text-3xl md:text-4xl text-[#042F2E] tracking-tight mb-2">
                  {codeType}
                </h1>
                {userName && <p className="text-[#25a1c2] font-medium text-lg">{userName}</p>}
                <p className="text-[#475569] text-xs mt-2">{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
              </div>

              <div className="space-y-6">
                {activeLetters.map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#25a1c2] to-[#042F2E] flex items-center justify-center shrink-0">
                      <span className="text-white font-bold text-sm">{item.letter}</span>
                    </div>
                    <div>
                      <p className="font-bold text-[#042F2E]">{item.word}</p>
                      <p className="text-[#475569] text-sm leading-relaxed mt-0.5">
                        {item.customDef || item.originalDef}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-[#CCFBF1] text-center">
                <p className="font-serif text-[#25a1c2] italic">"Change in your perspective changes your experience."</p>
                <p className="text-[#475569] text-xs mt-2">Created with the "I GET TO" Code Builder — mindbodyspirithygienetools.com</p>
                <p className="text-[#475569] text-xs mt-1">By Temple Franklin & the Divine Team</p>
              </div>
            </div>

            {/* Post-generation CTAs */}
            <div className="print:hidden mt-8 bg-[#F0F5F4] rounded-2xl p-6 text-center">
              <p className="text-[#25a1c2] font-medium text-sm mb-2">Love your code?</p>
              <p className="text-[#475569] text-xs mb-4">Go deeper with the tools that bring these codes to life:</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/" className="inline-flex items-center gap-1 bg-[#25a1c2] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-all">
                  <ShoppingBag className="w-3.5 h-3.5" /> Shop All Tools
                </Link>
                <Link to="/acronym-dictionary" className="inline-flex items-center gap-1 border-2 border-[#25a1c2] text-[#25a1c2] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#25a1c2] hover:text-white transition-all">
                  <BookOpen className="w-3.5 h-3.5" /> Full Dictionary
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
